import React, { useState, useEffect, useRef, useContext } from 'react';
import AppContext from 'context/Context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTags,
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faTimes,
  faSave,
  faTag
} from '@fortawesome/free-solid-svg-icons';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

const TagsSettingsSection = () => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const { token, nombreCompleto } = useToken();
  const { showSuccessToast, showErrorToast } = useToast();

  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const isInitialMount = useRef(true);

  // Modal Crear/Editar State
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Modal Eliminar State
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Cargar catálogo de etiquetas desde la API
  const fetchTags = async (searchQuery = searchTerm) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery && searchQuery.trim()) params.append('searchTerm', searchQuery.trim());

      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`/api/SupportEngine/GetEtiquetas${query}`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        }
      });

      if (response.ok) {
        const result = await response.json();
        const list = result.data || result;
        if (Array.isArray(list)) {
          setTags(list);
        }
      } else {
        showErrorToast('Error al cargar la lista de incidencias.');
      }
    } catch (err) {
      console.error('Error al cargar etiquetas:', err);
      showErrorToast('Error de conexión al cargar incidencias.');
    } finally {
      setIsLoading(false);
    }
  };

  // Cargar datos al montar y con debounce al buscar
  useEffect(() => {
    if (!token) return;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchTags(searchTerm);
      return;
    }

    setIsLoading(true);
    const timer = setTimeout(() => {
      fetchTags(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, token]);

  // Abrir modal para crear
  const handleOpenCreate = () => {
    setEditingId(null);
    setFormName('');
    setFormDescription('');
    setIsFormOpen(true);
  };

  // Abrir modal para editar
  const handleOpenEdit = tag => {
    setEditingId(tag.idEtiqueta);
    setFormName(tag.vNombre || '');
    setFormDescription(tag.vDescripcion || '');
    setIsFormOpen(true);
  };

  // Guardar (Crear o Actualizar)
  const handleSave = async e => {
    e.preventDefault();
    const cleanName = formName.trim();
    const cleanDesc = formDescription.trim();

    if (!cleanName) {
      showErrorToast('El nombre de la incidencia es obligatorio.');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        idEtiqueta: editingId || null,
        vNombre: cleanName,
        vDescripcion: cleanDesc || null,
        vCreatedUser: nombreCompleto || 'Sistema'
      };

      const response = await fetch('/api/SupportEngine/SaveEtiqueta', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        let msg = result.message || 'Error al guardar la incidencia';
        if (result.details) {
          try {
            const parsed = typeof result.details === 'string' ? JSON.parse(result.details) : result.details;
            if (parsed.message) msg = parsed.message;
          } catch (_) {
            msg = result.details;
          }
        }
        showErrorToast(msg);
        return;
      }

      showSuccessToast(editingId ? 'Incidencia actualizada exitosamente.' : 'Incidencia creada exitosamente.');
      setIsFormOpen(false);
      fetchTags(searchTerm);
    } catch (err) {
      console.error('Error al guardar incidencia:', err);
      showErrorToast('Error de conexión al guardar la incidencia.');
    } finally {
      setIsSaving(false);
    }
  };

  // Abrir confirmación de eliminación
  const handleOpenDelete = tag => {
    setItemToDelete(tag);
  };

  // Confirmar eliminación
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);

    try {
      const payload = {
        idEtiqueta: itemToDelete.idEtiqueta,
        vUpdatedUser: nombreCompleto || 'Sistema'
      };

      const response = await fetch('/api/SupportEngine/DeleteEtiqueta', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        showErrorToast('No se pudo desactivar la incidencia.');
        return;
      }

      showSuccessToast('Incidencia eliminada exitosamente.');
      setItemToDelete(null);
      fetchTags(searchTerm);
    } catch (err) {
      console.error('Error al eliminar etiqueta:', err);
      showErrorToast('Error de conexión al eliminar la incidencia.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div
      className="card border rounded-3 shadow-none mb-4"
      style={{
        backgroundColor: isDark ? '#101c26' : '#ffffff',
        borderColor: isDark ? '#233142' : '#e2e8f0'
      }}
    >
      {/* Header de la Sección */}
      <div className="card-header bg-transparent border-0 pb-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div>
          <h5 className="mb-1 d-flex align-items-center gap-2 fw-bold" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
            <div
              className="d-flex align-items-center justify-content-center rounded-circle"
              style={{
                width: '32px',
                height: '32px',
                backgroundColor: isDark ? 'rgba(59, 130, 246, 0.2)' : '#dbeafe',
                color: isDark ? '#60a5fa' : '#2563eb'
              }}
            >
              <FontAwesomeIcon icon={faTags} style={{ fontSize: '0.9rem' }} />
            </div>
            <span>Catálogo de Incidencias (Etiquetas)</span>
          </h5>
          <p className="small mb-0" style={{ fontSize: '0.84rem', color: isDark ? '#94a3b8' : '#64748b' }}>
            Crea y administra etiquetas para clasificar los problemas reportados en los tickets de soporte.
          </p>
        </div>

        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-primary rounded-pill px-3 shadow-none d-flex align-items-center gap-2 fw-semibold"
            style={{ backgroundColor: '#2563eb', borderColor: '#2563eb' }}
            onClick={handleOpenCreate}
          >
            <FontAwesomeIcon icon={faPlus} style={{ fontSize: '0.78rem' }} />
            <span>Nueva Incidencia</span>
          </button>
        </div>
      </div>

      {/* Barra de Búsqueda */}
      <div
        className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-2 border-top"
        style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}
      >
        <div
          className="d-flex align-items-center gap-2 px-3 py-1 border rounded-pill"
          style={{
            width: '370px',
            maxWidth: '100%',
            backgroundColor: isDark ? '#182635' : '#f8fafc',
            borderColor: isDark ? '#233142' : '#e2e8f0'
          }}
        >
          <FontAwesomeIcon icon={faSearch} className="text-muted" style={{ fontSize: '0.82rem' }} />
          <input
            type="text"
            className="border-0 bg-transparent shadow-none w-100"
            style={{ fontSize: '0.84rem', outline: 'none', color: isDark ? '#f9fafd' : '#0f172a' }}
            placeholder="Buscar incidencia por nombre o descripción..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              type="button"
              className="btn btn-sm p-0 text-muted border-0 bg-transparent"
              onClick={() => setSearchTerm('')}
            >
              <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.75rem' }} />
            </button>
          )}
        </div>

        <div className="text-muted small fw-medium" style={{ fontSize: '0.82rem' }}>
          Total: <span className="fw-bold" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>{tags.length}</span> incidencias activas
        </div>
      </div>

      {/* Contenido / Listado de Etiquetas */}
      <div className="card-body p-3 pt-2">
        {isLoading ? (
          <div className="py-5 text-center text-muted">
            <div className="spinner-border spinner-border-sm text-primary mb-2" role="status" />
            <div className="small">Cargando incidencias...</div>
          </div>
        ) : tags.length === 0 ? (
          <div
            className="text-center py-5 rounded-3 border border-dashed"
            style={{ borderColor: isDark ? '#233142' : '#cbd5e1' }}
          >
            <FontAwesomeIcon icon={faTag} className="text-muted mb-2" style={{ fontSize: '2rem', opacity: 0.4 }} />
            <h6 className="fw-semibold mb-1" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
              No se encontraron incidencias
            </h6>
            <p className="small text-muted mb-3" style={{ maxWidth: '400px', margin: '0 auto' }}>
              {searchTerm
                ? 'No hay resultados que coincidan con la búsqueda.'
                : 'Aún no has registrado ninguna etiqueta. Crea una para clasificar los problemas de los clientes.'}
            </p>
            {!searchTerm && (
              <button
                type="button"
                className="btn btn-sm btn-primary rounded-pill px-3 fw-semibold shadow-none"
                onClick={handleOpenCreate}
              >
                <FontAwesomeIcon icon={faPlus} className="me-1" /> Crear Primera Incidencia
              </button>
            )}
          </div>
        ) : (
          <div className="row g-3">
            {tags.map(tag => (
              <div key={tag.idEtiqueta} className="col-12 col-md-6 col-lg-4">
                <div
                  className="p-3 rounded-3 border h-100 d-flex flex-column justify-content-between transition-all"
                  style={{
                    backgroundColor: isDark ? '#142230' : '#f8fafc',
                    borderColor: isDark ? '#233142' : '#e2e8f0',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                  }}
                >
                  <div>
                    {/* Header de la tarjeta con Badge y Acciones */}
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span
                        className="badge px-3 py-1 rounded-pill fw-semibold d-inline-flex align-items-center"
                        style={{
                          backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                          color: isDark ? '#cbd5e1' : '#334155',
                          border: `1px solid ${isDark ? '#334155' : '#cbd5e1'}`,
                          fontSize: '0.82rem',
                          gap: '8px',
                          maxWidth: 'calc(100% - 60px)',
                          overflow: 'hidden'
                        }}
                        title={tag.vNombre}
                      >
                        <FontAwesomeIcon icon={faTag} style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#64748b' }} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {tag.vNombre}
                        </span>
                      </span>

                      <div className="d-flex align-items-center gap-1">
                        <button
                          type="button"
                          className="btn btn-sm p-1 text-muted border-0 bg-transparent hover-opacity"
                          title="Editar incidencia"
                          onClick={() => handleOpenEdit(tag)}
                        >
                          <FontAwesomeIcon icon={faEdit} style={{ fontSize: '0.85rem' }} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm p-1 text-danger border-0 bg-transparent hover-opacity"
                          title="Eliminar incidencia"
                          onClick={() => handleOpenDelete(tag)}
                        >
                          <FontAwesomeIcon icon={faTrash} style={{ fontSize: '0.85rem' }} />
                        </button>
                      </div>
                    </div>

                    {/* Descripción */}
                    <p
                      className="small mb-0"
                      style={{
                        fontSize: '0.84rem',
                        color: isDark ? '#94a3b8' : '#64748b',
                        minHeight: '2.4em'
                      }}
                    >
                      {tag.vDescripcion || <span className="fst-italic opacity-50">Sin descripción</span>}
                    </p>
                  </div>

                  {/* Footer con info de autor */}
                  <div
                    className="pt-2 mt-2 border-top d-flex align-items-center justify-content-between"
                    style={{ borderColor: isDark ? '#1e2c3a' : '#e2e8f0', fontSize: '0.74rem', color: isDark ? '#64748b' : '#94a3b8' }}
                  >
                    <span>ID: #{tag.idEtiqueta}</span>
                    <span>{tag.vCreatedUser ? `Por: ${tag.vCreatedUser}` : ''}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MODAL CREAR / EDITAR */}
      {isFormOpen && (
        <div
          className="modal d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1050 }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '520px' }}>
            <div
              className="modal-content border shadow-lg"
              style={{
                backgroundColor: isDark ? '#182635' : '#ffffff',
                borderColor: isDark ? '#233142' : '#cbd5e1'
              }}
            >
              <div className="modal-header border-bottom px-4 py-3" style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}>
                <h6 className="modal-title fw-bold d-flex align-items-center gap-2" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                  <FontAwesomeIcon icon={editingId ? faEdit : faTag} className="text-primary" />
                  <span>{editingId ? 'Editar Incidencia' : 'Nueva Incidencia'}</span>
                </h6>
                <button
                  type="button"
                  className="btn-close shadow-none"
                  style={{ filter: isDark ? 'invert(1) grayscale(100%)' : 'none' }}
                  onClick={() => setIsFormOpen(false)}
                />
              </div>

              <form onSubmit={handleSave}>
                <div className="modal-body px-4 py-3">
                  {/* Nombre */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <label className="form-label small fw-semibold mb-0" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                        Nombre de la Incidencia <span className="text-danger">*</span>
                      </label>
                      <span className="small text-muted" style={{ fontSize: '0.74rem' }}>
                        {formName.length}/30 caracteres
                      </span>
                    </div>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      style={{
                        backgroundColor: isDark ? '#101c26' : '#ffffff',
                        borderColor: isDark ? '#233142' : '#cbd5e1',
                        color: isDark ? '#f9fafd' : '#0f172a'
                      }}
                      placeholder="Ej. Error de Facturación, Duda de Cobro..."
                      maxLength={30}
                      value={formName}
                      onChange={e => setFormName(e.target.value)}
                      required
                    />
                  </div>

                  {/* Descripción */}
                  <div className="mb-3">
                    <label className="form-label small fw-semibold" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                      Descripción <span className="text-muted fw-normal">(Opcional)</span>
                    </label>
                    <textarea
                      className="form-control form-control-sm"
                      rows={3}
                      style={{
                        backgroundColor: isDark ? '#101c26' : '#ffffff',
                        borderColor: isDark ? '#233142' : '#cbd5e1',
                        color: isDark ? '#f9fafd' : '#0f172a'
                      }}
                      placeholder="Breve explicación de cuándo clasificar un ticket con esta etiqueta..."
                      maxLength={200}
                      value={formDescription}
                      onChange={e => setFormDescription(e.target.value)}
                    />
                  </div>

                  {/* Preview del Badge */}
                  <div className="mb-3">
                    <div
                      className="p-3 rounded-3 border d-flex align-items-center justify-content-between"
                      style={{
                        backgroundColor: isDark ? '#101c26' : '#f8fafc',
                        borderColor: isDark ? '#233142' : '#e2e8f0',
                        minHeight: '52px'
                      }}
                    >
                      <span className="small text-muted fw-medium" style={{ fontSize: '0.82rem' }}>Vista previa:</span>
                      <span
                        className="badge px-3 py-1.5 rounded-pill fw-semibold d-inline-flex align-items-center"
                        style={{
                          backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                          color: isDark ? '#cbd5e1' : '#334155',
                          border: `1px solid ${isDark ? '#334155' : '#cbd5e1'}`,
                          fontSize: '0.84rem',
                          gap: '6px',
                          maxWidth: '300px',
                          overflow: 'hidden'
                        }}
                      >
                        <FontAwesomeIcon icon={faTag} style={{ fontSize: '0.72rem', opacity: 0.7 }} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {formName.trim() || 'Nombre de la Incidencia'}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="modal-footer border-top px-4 py-3 d-flex align-items-center justify-content-end gap-2" style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}>
                  <button
                    type="button"
                    className="btn rounded-pill px-3 py-1.5 shadow-none"
                    style={{
                      fontSize: '0.84rem',
                      color: isDark ? '#94a3b8' : '#64748b',
                      border: isDark ? '1px solid #233142' : '1px solid #cbd5e1',
                      backgroundColor: 'transparent'
                    }}
                    onClick={() => setIsFormOpen(false)}
                    disabled={isSaving}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary rounded-pill px-4 py-1.5 shadow-none d-flex align-items-center justify-content-center fw-semibold"
                    style={{
                      gap: '8px',
                      fontSize: '0.84rem',
                      backgroundColor: '#2563eb',
                      borderColor: '#2563eb'
                    }}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" />
                        <span>Guardando...</span>
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSave} style={{ fontSize: '0.85rem' }} />
                        <span>{editingId ? 'Guardar Cambios' : 'Crear Incidencia'}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ELIMINAR */}
      {itemToDelete && (
        <div
          className="modal d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1050 }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '420px' }}>
            <div
              className="modal-content border shadow-lg"
              style={{
                backgroundColor: isDark ? '#182635' : '#ffffff',
                borderColor: isDark ? '#233142' : '#cbd5e1'
              }}
            >
              <div className="modal-body px-4 py-4 text-center">
                <div
                  className="rounded-circle mx-auto d-flex align-items-center justify-content-center mb-3 text-danger"
                  style={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: isDark ? 'rgba(239, 68, 68, 0.15)' : '#fee2e2'
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} style={{ fontSize: '1.2rem' }} />
                </div>
                <h6 className="fw-bold mb-2" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                  ¿Eliminar Incidencia?
                </h6>
                <p className="small text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                  ¿Estás seguro de que deseas eliminar la incidencia{' '}
                  <strong style={{ color: isDark ? '#f8fafc' : '#0f172a' }}>"{itemToDelete.vNombre}"</strong>?
                  Esta etiqueta ya no aparecerá en el catálogo para clasificar nuevos tickets.
                </p>

                <div className="d-flex align-items-center justify-content-center gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary rounded-pill px-3 shadow-none"
                    onClick={() => setItemToDelete(null)}
                    disabled={isDeleting}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger rounded-pill px-4 shadow-none fw-semibold"
                    onClick={handleConfirmDelete}
                    disabled={isDeleting}
                  >
                    {isDeleting ? 'Eliminando...' : 'Sí, Eliminar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagsSettingsSection;
